import { Button, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CompanyInfoSession from "../Component/CompanyInfoSection";
import MerchantInfoSession from "../Component/MerchantInfoSection";
import Divider from "components/Divider/Divider";
import AccountInfoSession from "../Component/AccountInfoSection";
import { useTranslation } from "react-i18next";
import { updateMerchant, updateMerchantOtherInfo } from "../model";
import { useSnackbar } from "notistack";
import { INFO_KEYS, pickTheFieldsFrom, filterDifferences } from "../utils";
import useFormUndo from "utils/useFormUndo/useFormUndo";
import { formPreventEnterSubmit } from "utils/utils";
import ButtonMAS from "ButtonMAS/ButtonMAS";

const PAGE_LOCALE_KEY = "page.merchants.merchantDetail.";

const MerchantInfo = ({
  merchant,
  merchantOtherDetail,
  dropdownOptions,
  staticMerchantPaylaod,
  fetchMerchantAndMerchantOtherDetail,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const formProps = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    reset,
    setValue,
  } = formProps;
  const { rememberForm, undoForm, clearFormMemory } = useFormUndo({ formProps });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [originalLogo, setOriginalLogo] = useState(null);

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  useEffect(() => {
    if (Object.keys(merchant).length > 0) {
      console.log("[INFO] merchant: ", merchant);
      const initialValue = {
        ...pickTheFieldsFrom(merchant, INFO_KEYS()),
        ...pickTheFieldsFrom(merchantOtherDetail, INFO_KEYS()),
      };
      console.log("[INFO]initialValue: ", initialValue);
      setAllValues(initialValue);
    }
  }, [merchant]);

  const onSubmit = async (formValue) => {
    const MERCHANT_OTHER_INFO_KEYS = [
      // "onboardSales",
      // "sales",
      // "referrer",
      "bankAccount",
      "beneficiary",
      "bankAccountRemarks",
    ];

    try {
      setIsLoading(true);

      const apiConfigs = [
        {
          key: "MERCHANT",
          diff: filterDifferences(
            merchant,
            formValue,
            null,
            MERCHANT_OTHER_INFO_KEYS,
          ),
          payload: {
            ...staticMerchantPaylaod(merchant, "info"),
            ...filterDifferences(
              merchant,
              formValue,
              null,
              MERCHANT_OTHER_INFO_KEYS,
            ),
          },
          apiCall: updateMerchant,
        },
        {
          key: "MERCHANT_OTHER_INFO",
          diff: filterDifferences(
            merchantOtherDetail,
            formValue,
            MERCHANT_OTHER_INFO_KEYS,
            [],
          ),
          payload: {
            ...filterDifferences(
              merchantOtherDetail,
              formValue,
              MERCHANT_OTHER_INFO_KEYS,
              [],
            ),
            merchantId: merchant.merchantId,
            id: merchantOtherDetail?.id,
            // ...(formValue.sales && { sales: { id: formValue.sales } }),
            // ...(formValue.onboardSales && {
            //   onboardSales: { id: formValue.onboardSales },
            // }),
            // ...(formValue.referrer && { referrer: { id: formValue.referrer } }),
          },
          apiCall: updateMerchantOtherInfo,
        },
      ];

      const validApis = apiConfigs.filter((config) => {
        console.log(`${config.key} config.diff`, config.diff);
        return Object.keys(config.diff).length > 0;
      });

      if (validApis.length === 0) {
        snackbar(t(PAGE_LOCALE_KEY + "fetchMessage.noChanges"), "info");
        return;
      }

      const [responses, errors] = await Promise.allSettled(
        validApis.map((config) => config.apiCall(config.payload)),
      ).then((results) => {
        const res = {};
        const errs = {};

        results.forEach((result, index) => {
          const key = validApis[index].key;
          if (result.status === "fulfilled") {
            res[key] = result.value;
          } else {
            errs[key] = result.reason;
            console.error(`${key} error:`, result.reason);
          }
        });

        return [res, errs];
      });

      let hasError = false;

      validApis.forEach(({ key }) => {
        const response = responses[key];
        const error = errors[key];

        if (error) {
          hasError = true;
          return snackbar(
            `${t(PAGE_LOCALE_KEY + "fetchMessage.updateFailed")}: ${error.message}`,
            "error",
          );
        }

        if (response?.status === 401) {
          console.log("Unauthorized access");
          hasError = true;
          return logout();
        }

        if (response?.status === 403) {
          hasError = true;
          return snackbar(t("general.message.unauthorized"), "error");
        }

        const respBody = response?.data;
        if (respBody?.responseCode !== "0000") {
          hasError = true;
          return snackbar(
            `${t(PAGE_LOCALE_KEY + "fetchMessage.updateFailed")}: ${respBody?.message || "Unknown error"}`,
            "error",
          );
        }
      });

      if (!hasError && validApis.length > 0) {
        snackbar(t(PAGE_LOCALE_KEY + "fetchMessage.updateSuccess"), "success");
      }
      await fetchMerchantAndMerchantOtherDetail();
    } catch (error) {
      console.error("Submission error:", error);
      snackbar(
        `${t(PAGE_LOCALE_KEY + "fetchMessage.updateFailed")}: ${error.message}`,
        "error",
      );
    } finally {
      setIsEditing(false);
      setIsLoading(false);
      clearFormMemory();
    }
  };
  const appendBase64Data = (base64String) => {
    if (base64String?.startsWith("iVBORw0KGgo")) {
      base64String = "data:image/png;base64," + base64String;
    }
    if (base64String?.startsWith("/9j/")) {
      base64String = "data:image/jpeg;base64," + base64String;
    }
    return base64String;
  };

  const setAllValues = (initialValue) => {
    const logoValue = appendBase64Data(initialValue?.logo);
    setOriginalLogo(logoValue); // Store original logo value
    reset(
      {
        ...initialValue,
        shareholderInfo:
          typeof initialValue?.shareholderInfo === "string"
            ? JSON.parse(initialValue?.shareholderInfo ?? "[]")
            : initialValue?.shareholderInfo,
        logo: logoValue,
        // referrer: initialValue?.referrer?.id,
        // onboardSales: initialValue?.onboardSales?.id,
        // sales: initialValue?.sales?.id,
      },
      {
        keepDefaultValues: false,
        keepDirty: false,
        keepErrors: false,
      },
    );
  };

  const submitButtons = () => (
    isEditing && <Stack
      direction="row"
      justifyContent="flex-end"
      gap={2}
    >
      <Button
        variant="outline-normal-button"
        type="button" // Add type="button" to prevent form submission
        onClick={() => {
          // Restore original logo when canceling (only if there was one)
          originalLogo
            ? setValue("logo", originalLogo)
            : setValue("logo", null);
          setIsEditing(false);
          undoForm();
        }}
        disabled={isLoading}
      >
        {t(`${PAGE_LOCALE_KEY}button.cancel`)}
      </Button>
      <ButtonMAS
        type="submit"
        isLoading={isLoading}
        disabled={isLoading}
      >
        {t(`${PAGE_LOCALE_KEY}button.save`)}
      </ButtonMAS>
    </Stack>
  )

  return (
    <Stack direction="column" sx={{ padding: "24px", background: "white" }}>
      <Button
        variant="normal-button"
        sx={{
          alignSelf: "flex-end",
          display: isEditing ? "" : "block",
          visibility: isEditing ? "hidden" : "",
        }}
        onClick={() => {
          setIsEditing(true);
          rememberForm();
        }}
      >
        {t(`${PAGE_LOCALE_KEY}button.edit`)}
      </Button>

      <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
        <CompanyInfoSession
          formProps={formProps}
          isLoading={isLoading}
          isEdit={isEditing}
          mccDropdownOptions={dropdownOptions?.mccList}
        />
        <Divider style={{ width: "80%", margin: "24px 0" }} />

        <MerchantInfoSession
          formProps={formProps}
          isLoading={isLoading}
          isEdit={isEditing}
        />
        <Divider style={{ width: "80%", margin: "24px 0" }} />

        {/* <OthersSession
          formProps={formProps}
          isLoading={isLoading}
          isEdit={isEditing}
          dropdownOptions={dropdownOptions}
        /> */}
        {/* <Divider style={{ width: "80%", margin: "24px 0" }} /> */}

        <AccountInfoSession
          formProps={formProps}
          isLoading={isLoading}
          isEdit={isEditing}
        />
        <Divider style={{ width: "80%", margin: "24px 0" }} />
        {submitButtons()}
      </form>
    </Stack>
  );
};

export default React.memo(MerchantInfo);
